import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Loader2 } from 'lucide-react';
import { shortcutOptions, MAX_SHORTCUTS } from '../config/pageMeta';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { getMyAbilities } from '../services/rbacService';
import { pathToPermissionKey } from '../utils/permissions';
import { useAuth } from '../contexts/AuthContext';

const LONG_PRESS_MS = 3000;
const LONG_DELETE_MS = 4500;
const SHORTCUT_FETCH_RETRY_MS = 450;
const SHORTCUT_FETCH_MAX_ATTEMPTS = 3;

const ShortcutSlot = ({
  option,
  isActive,
  hasValue,
  onPrimaryClick,
  onEmptyClick,
  onLongPressEdit,
  onLongPressDelete,
  disabled
}) => {
  const pressTimerRef = useRef(null);
  const deleteTimerRef = useRef(null);
  const longPressEditTriggeredRef = useRef(false);
  const longPressDeleteTriggeredRef = useRef(false);

  const handlePointerDown = (event) => {
    if (!hasValue || disabled) return;
    if (event.button !== undefined && event.button !== 0) return;
    longPressEditTriggeredRef.current = false;
    longPressDeleteTriggeredRef.current = false;
    pressTimerRef.current = window.setTimeout(() => {
      longPressEditTriggeredRef.current = true;
      onLongPressEdit();
    }, LONG_PRESS_MS);
    deleteTimerRef.current = window.setTimeout(() => {
      longPressDeleteTriggeredRef.current = true;
      onLongPressDelete();
    }, LONG_DELETE_MS);
  };

  const clearTimers = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    if (deleteTimerRef.current) {
      clearTimeout(deleteTimerRef.current);
      deleteTimerRef.current = null;
    }
  };

  const handlePointerUp = (event) => {
    clearTimers();
    if (!hasValue) return;
    if (longPressDeleteTriggeredRef.current) {
      longPressDeleteTriggeredRef.current = false;
      return;
    }
    if (longPressEditTriggeredRef.current) {
      longPressEditTriggeredRef.current = false;
      return;
    }
  };

  const handleClick = (event) => {
    const wasLongPress = longPressEditTriggeredRef.current || longPressDeleteTriggeredRef.current;
    clearTimers();
    if (wasLongPress) {
      longPressEditTriggeredRef.current = false;
      longPressDeleteTriggeredRef.current = false;
      event.preventDefault();
      return;
    }
    if (!hasValue) {
      event.preventDefault();
      onEmptyClick?.();
      return;
    }

    onPrimaryClick?.();
  };

  const handlePointerLeave = (event) => {
    clearTimers();
    longPressEditTriggeredRef.current = false;
    longPressDeleteTriggeredRef.current = false;
  };

  const Icon = option?.icon;

  return (
    <button
      type="button"
      title={option?.title || 'Kısayol ekle'}
      className={`
        w-9 h-9 rounded-full flex items-center justify-center transition-colors
        ${hasValue
          ? isActive
            ? 'bg-blue-600 text-white shadow'
            : 'bg-white/90 dark:bg-gray-800 text-gray-600 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-700'
          : 'bg-gray-100/70 dark:bg-gray-800 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}
      `}
      onPointerDown={hasValue ? handlePointerDown : undefined}
      onPointerUp={hasValue ? handlePointerUp : undefined}
      onPointerLeave={hasValue ? handlePointerLeave : undefined}
      onClick={handleClick}
      disabled={disabled}
      aria-label={option?.title || 'Kısayol ekle'}
    >
      {hasValue && Icon ? <Icon className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
    </button>
  );
};

const ShortcutBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [shortcuts, setShortcuts] = useState(Array(MAX_SHORTCUTS).fill(null));
  const [pickerIndex, setPickerIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [abilities, setAbilities] = useState(null);

  const sleep = useCallback((ms) => new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  }), []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const abilityMap = await getMyAbilities();
        if (mounted) {
          setAbilities(abilityMap || {});
        }
      } catch {
        if (mounted) setAbilities({});
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const canUsePath = useCallback((path) => {
    if (!path) return false;
    if (!abilities) return false;
    const key = pathToPermissionKey(path);
    return !!abilities[key];
  }, [abilities]);

  useEffect(() => {
    const fetchShortcuts = async () => {
      let lastError = null;
      try {
        setLoading(true);
        for (let attempt = 1; attempt <= SHORTCUT_FETCH_MAX_ATTEMPTS; attempt += 1) {
          try {
            const response = await api.get('/api/users/shortcuts');
            const data = Array.isArray(response.data?.data) ? response.data.data : [];
            const next = Array(MAX_SHORTCUTS).fill(null);
            data.slice(0, MAX_SHORTCUTS).forEach((path, index) => {
              const normalized = typeof path === 'string' ? path : null;
              next[index] = normalized && canUsePath(normalized) ? normalized : null;
            });
            setShortcuts(next);
            lastError = null;
            break;
          } catch (error) {
            lastError = error;
            const statusCode = Number(error?.response?.status || 0);
            const isLastAttempt = attempt === SHORTCUT_FETCH_MAX_ATTEMPTS;
            if (statusCode === 401 || statusCode === 403) {
              // Token yenilenme/ilk mount yarışında gereksiz toast göstermeyelim.
              break;
            }
            if (!isLastAttempt) {
              await sleep(SHORTCUT_FETCH_RETRY_MS * attempt);
            }
          }
        }
      } catch (error) {
        lastError = error;
      } finally {
        if (lastError) {
          // Kalıcı hata varsa bildir; UI yine boş kısayolla kullanılabilir kalsın.
          toast.error('Kısayollar yüklenemedi');
        }
        setLoading(false);
      }
    };

    fetchShortcuts();
  }, [canUsePath, sleep]);

  useEffect(() => {
    if (!abilities) return;
    setShortcuts((prev) => prev.map((path) => (path && canUsePath(path) ? path : null)));
  }, [abilities, canUsePath]);

  const optionMap = useMemo(() => {
    const map = new Map();
    shortcutOptions.forEach((option) => {
      map.set(option.path, option);
    });
    return map;
  }, []);

  const filteredOptions = useMemo(() => {
    const lowerSearch = searchTerm.trim().toLowerCase();
    const usedPaths = new Set(shortcuts.filter(Boolean));
    const activePath = pickerIndex !== null ? shortcuts[pickerIndex] : null;
    const isAdmin = user?.role === 'admin';

    return shortcutOptions
      .filter((option) => !option.adminOnly || isAdmin)
      .filter((option) => canUsePath(option.path))
      .filter((option) => {
        if (option.path === activePath) {
          return true;
        }
        return !usedPaths.has(option.path);
      })
      .filter((option) => {
        if (!lowerSearch) return true;
        return option.title.toLowerCase().includes(lowerSearch);
      })
      .reduce((groups, option) => {
        const key = option.category || 'Diğer';
        if (!groups[key]) groups[key] = [];
        groups[key].push(option);
        return groups;
      }, {});
  }, [shortcuts, pickerIndex, searchTerm, canUsePath, user?.role]);

  const persistShortcuts = async (next, previous) => {
    try {
      setSaving(true);
      await api.put('/api/users/shortcuts', {
        shortcuts: next.filter(Boolean)
      });
    } catch (error) {
      setShortcuts(previous);
      toast.error(error.response?.data?.message || 'Kısayollar kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const slots = useMemo(() => shortcuts, [shortcuts]);

  const handleSlotRemove = (index, silent = false) => {
    setShortcuts((prev) => {
      if (!prev[index]) return prev;
      const next = [...prev];
      next[index] = null;
      persistShortcuts(next, prev);
      return next;
    });
    if (!silent) {
      toast.success('Kısayol kaldırıldı');
    }
  };

  if (loading && !shortcuts.some(Boolean)) {
    return <div className="hidden lg:flex items-center text-xs text-gray-500">Kısayollar yükleniyor...</div>;
  }

  return (
    <>
      <div className="hidden lg:flex items-center w-full">
        <div className="flex items-center gap-2 bg-white/95 dark:bg-gray-900/70 border border-gray-200/70 dark:border-gray-700/60 rounded-2xl px-3 py-1.5 shadow-sm">
          {slots.map((path, idx) => {
            const option = optionMap.get(path);
            const isActive = path && location.pathname.startsWith(path);

            return (
              <ShortcutSlot
                key={idx}
                option={option}
                isActive={!!isActive}
                hasValue={!!path}
                disabled={saving}
                onEmptyClick={() => setPickerIndex(idx)}
                onLongPressEdit={() => setPickerIndex(idx)}
                onLongPressDelete={() => handleSlotRemove(idx)}
                onPrimaryClick={() => {
                  if (!path) return;
                  const navEvent = new CustomEvent('app:navigate-attempt', {
                    detail: { href: path },
                    cancelable: true
                  });
                  const allowed = window.dispatchEvent(navEvent);
                  if (!allowed) {
                    return;
                  }
                  navigate(path);
                }}
              />
            );
          })}
        </div>
        {saving && (
          <Loader2 className="ml-2 w-4 h-4 text-blue-500 animate-spin" />
        )}
      </div>

      {pickerIndex !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Kısayol Seç</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Bir menü seçin. Dolu bir kısayolu düzenlemek için simgeye 3 sn basılı tutabilir, daha uzun basılı tutarak silebilirsiniz.</p>
              </div>
              <button className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900" onClick={() => setPickerIndex(null)}>
                Kapat
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Kısayol ara..."
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
                />
                {shortcuts[pickerIndex] && (
                  <button
                    onClick={() => {
                      handleSlotRemove(pickerIndex);
                      setPickerIndex(null);
                      setSearchTerm('');
                    }}
                    className="px-3 py-2 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 dark:bg-red-900/30 dark:text-red-200"
                  >
                    Kaldır
                  </button>
                )}
              </div>

              <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1">
                {Object.entries(filteredOptions).map(([category, options]) => (
                  <div key={category}>
                    <p className="text-xs font-semibold tracking-wide text-gray-500 dark:text-gray-400 uppercase mb-2">{category}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {options.map((option) => {
                        const Icon = option.icon;
                        return (
                          <button
                            key={option.path}
                            onClick={() => {
                              const prev = shortcuts;
                              const next = [...prev];
                              next[pickerIndex] = option.path;
                              setPickerIndex(null);
                              setSearchTerm('');
                              setShortcuts(next);
                              persistShortcuts(next, prev);
                            }}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-gray-800 text-sm text-gray-800 dark:text-gray-200"
                          >
                            <Icon className="w-4 h-4 text-blue-500" />
                            <span>{option.title}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {Object.keys(filteredOptions).length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center">Seçilebilecek kısayol bulunamadı.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShortcutBar;


